import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

type EmailTemplateType = 'standard' | 'sothebys' | 'wallace';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  templateId: EmailTemplateType;
  domain?: string;
  logoUrl?: string;
  primaryColor?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateTenantInput {
  name: string;
  slug: string;
  templateId?: EmailTemplateType;
  domain?: string;
  logoUrl?: string;
  primaryColor?: string;
  isActive?: boolean;
}

export function useTenants() {
  return useQuery<Tenant[]>({
    queryKey: ['admin', 'tenants'],
    queryFn: async () => {
      const response = await fetch('/api/admin/tenants');
      if (!response.ok) throw new Error('Failed to fetch tenants');
      return response.json();
    },
  });
}

export function useCreateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTenantInput) => {
      const response = await fetch('/api/admin/tenants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to create tenant');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateTenantInput> }) => {
      const response = await fetch(`/api/admin/tenants/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update tenant');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}
